import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Star, X } from "lucide-react";

// Note du repas / restaurant — spécifique Livraison Repas Nold Go.
// Séparée de la note du chauffeur pour laisser le client évaluer QUALITÉ (repas) et LIVREUR indépendamment.
export default function FoodRatingSheet({ ride, onClose, onRated }) {
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!stars) return toast.error("Choisissez une note (1 à 5).");
    setSaving(true);
    try {
      await api.post(`/rides/${ride.id}/food-rate`, { rating: stars, comment: comment.trim() || null });
      toast.success("Merci pour votre note du repas 🍔");
      onRated && onRated();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setSaving(false); }
  };

  const labels = ["", "Très déçu", "Décevant", "Correct", "Très bon", "Excellent"];
  const shown = hover || stars;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1000] bg-[#1A1A1A]/50 backdrop-blur-sm flex items-end sm:items-center sm:justify-center"
      onClick={onClose}
      data-testid="food-rating-overlay"
    >
      <motion.div
        initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="bg-[#FDF8F0] w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-6 pb-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="food-rating-modal"
      >
        <div className="flex justify-between items-start">
          <div>
            <h2 className="font-head font-black text-2xl tracking-tight text-[#1A1A1A]">🍔 Noter le repas</h2>
            <p className="mt-1 text-sm text-[#75807A]">
              {ride.restaurant_name ? <>Restaurant : <span className="font-semibold text-[#16a34a]">{ride.restaurant_name}</span></> : "Comment était votre repas ?"}
            </p>
          </div>
          <button data-testid="food-rating-close" onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-[#F0EBE1] flex items-center justify-center">
            <X className="w-5 h-5 text-[#75807A]" />
          </button>
        </div>

        <div className="mt-6 flex items-center justify-center gap-3">
          {[1, 2, 3, 4, 5].map((s) => (
            <button
              key={s}
              data-testid={`food-star-${s}`}
              onMouseEnter={() => setHover(s)}
              onMouseLeave={() => setHover(0)}
              onClick={() => setStars(s)}
              className="transition-transform hover:scale-110 active:scale-95"
            >
              <Star className={`w-11 h-11 ${shown >= s ? "fill-[#16a34a] text-[#16a34a]" : "text-[#E2DFD8]"}`} strokeWidth={1.5} />
            </button>
          ))}
        </div>
        <p className="mt-3 text-center text-sm font-semibold text-[#16a34a] h-5">{labels[shown] || ""}</p>

        <textarea
          data-testid="food-rating-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Un mot sur le repas (facultatif)…"
          rows={3}
          className="mt-4 w-full rounded-xl border-2 border-[#E2DFD8] p-4 text-sm focus:outline-none focus:border-[#16a34a] resize-none"
        />

        <button
          data-testid="food-rating-submit"
          onClick={submit}
          disabled={saving}
          className="mt-4 w-full h-14 rounded-xl bg-[#16a34a] hover:bg-[#128a3e] text-white font-bold text-lg disabled:opacity-60 transition-transform hover:-translate-y-0.5"
        >
          {saving ? "Envoi…" : "Envoyer ma note"}
        </button>
        <button data-testid="food-rating-skip" onClick={onClose} className="mt-2 w-full h-11 text-sm text-[#75807A] font-medium">
          Passer
        </button>
      </motion.div>
    </motion.div>
  );
}
